import { Tooltip } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import type { NamePath, StoreValue } from 'rc-field-form/es/interface';
import React, { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';

import { useLabelValueCellStore } from '../../../../hooks';
import type { ExperienceOpening } from '../../../../utils/experiences';
import { DuplicationMenu } from '../../../DuplicationMenu/DuplicationMenu';

import { EditableNumberField } from './EditableNumberField';
import styles from './LabelValueCell.module.scss';
import { MemoField } from './MemoField';
import { onHover } from './utils';

type BaseLabelValueCellProps = {
  label?: React.ReactNode;
  value: string | number | React.ReactNode;
  className?: string;
  labelClassName?: string;
  valueClassName?: string;
  labelPosition?: 'top' | 'bottom';
  status?: ExperienceOpening['inventoryStatus'];
  statusLabel?: string;
  tooltip?: React.ReactNode;
  name: NamePath;
  date: string;
  disabled?: boolean;
  grayed?: boolean;
  formatter?: (
    v: number | undefined,
    currencyFormatter: (val: number) => string
  ) => React.ReactNode;
  getValueFromEvent?: (e: number | string) => StoreValue;
  getValueProps?: (value: number | string) => Record<string, number | string>;
  max?: number;
};

type EditableLabelValueCellProps = BaseLabelValueCellProps & {
  isEditable: true;
  onDuplicate: () => void;
};

type NonEditableLabelValueCellProps = BaseLabelValueCellProps & {
  isEditable?: false | undefined;
  onDuplicate?: () => void;
};

export type LabelValueCellProps =
  EditableLabelValueCellProps | NonEditableLabelValueCellProps;

const LabelValueCellComponent: React.FC<LabelValueCellProps> = (props) => {
  const {
    label,
    value,
    className = '',
    labelClassName = '',
    valueClassName = '',
    labelPosition = 'top',
    status,
    statusLabel,
    tooltip,
    name,
    date,
    disabled = false,
    grayed = false,
    formatter,
    getValueFromEvent,
    getValueProps,
    onDuplicate,
    isEditable,
    max,
  } = props;

  const { isEditMode, timePeriod, setDuplicationDateSelection } =
    useLabelValueCellStore();

  const { currencyFormatter } = useContext(CurrencyContext);

  const showInput = isEditMode && isEditable;

  const content = (
    <div
      className={classNames(
        styles.labelValueCell,
        {
          [styles.labelBottom]: labelPosition === 'bottom',
          [styles.unpublished]: status === 'UNPUBLISHED',
          [styles.disabled]: disabled || grayed,
        },
        className
      )}
    >
      <>
        {label && (
          <span className={classNames(styles.label, labelClassName)}>
            {label}
          </span>
        )}
        {showInput ? (
          <div className={styles.editableCell}>
            <EditableNumberField
              name={name}
              valueClassName={valueClassName}
              disabled={disabled}
              max={max}
              getValueFromEvent={getValueFromEvent}
              getValueProps={getValueProps}
            />
            {isEditable && onDuplicate && (
              <DuplicationMenu
                date={dayjs(date)}
                currentRange={{
                  start: dayjs(timePeriod.startDate),
                  end: dayjs(timePeriod.endDate),
                }}
                onDuplicate={onDuplicate}
                onHover={(day, mode) => {
                  onHover(day, mode, timePeriod, setDuplicationDateSelection);
                }}
                onHoverEnd={() => setDuplicationDateSelection([])}
                disabled={disabled}
              />
            )}
          </div>
        ) : (
          <MemoField<number | undefined> name={name}>
            {(fieldValue) => {
              return (
                <span className={classNames(styles.value, valueClassName)}>
                  {formatter
                    ? formatter(fieldValue, currencyFormatter)
                    : (fieldValue ?? value)}
                </span>
              );
            }}
          </MemoField>
        )}
      </>
    </div>
  );

  const tooltipContent =
    tooltip || (status && statusLabel ? statusLabel : null);

  if (!tooltipContent || showInput) {
    return content;
  }

  return (
    <Tooltip overlayClassName="extranetTooltip" title={tooltipContent}>
      {content}
    </Tooltip>
  );
};

export const LabelValueCell = React.memo(LabelValueCellComponent);
